// ManagerApproval.js - COMPLETE FIXED VERSION

import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { Host } from '../../App';
import './ManagerApproval.css';

const ManagerApproval = () => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [approvedRequests, setApprovedRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [approvalData, setApprovalData] = useState({
    assigned_completion_date: '',
    manager_comments: '',
    rejection_reason: ''
  });
  const [actionType, setActionType] = useState('approve');

  const token = window.localStorage.getItem('acessToken');
  const userName = JSON.parse(window.localStorage.getItem('Name'));

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      const response = await Axios.get(`https://` + Host + `/maintenanc/api/tickets/approval-list`, {
        params: { token }
      });
      if (response.data) {
        console.log('Fetched requests:', response.data);
        setPendingRequests(response.data.pending || []);
        setApprovedRequests(response.data.approved || []);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
      window.alert('Failed to fetch requests');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setActionType('approve');
    setApprovalData({
      assigned_completion_date: '',
      manager_comments: '',
      rejection_reason: ''
    });
    setShowModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setActionType('reject');
    setApprovalData({
      assigned_completion_date: '',
      manager_comments: '',
      rejection_reason: ''
    });
    setShowModal(true);
  };

  const handleApprovalChange = (e) => {
    const { name, value } = e.target;
    setApprovalData(prev => ({ ...prev, [name]: value }));
  };

  const submitApproval = async () => {
    if (!selectedRequest) return;

    setIsLoading(true);
    try {
      const payload = {
        ticketId: selectedRequest.id,
        action: actionType,
        approved_by: userName,
        ...approvalData
      };

      const response = await Axios.post(`https://` + Host + `/maintenance/api/tickets/approve`, {
        token,
        ...payload
      });

      if (response.status === 200) {
        window.alert(`Request ${actionType === 'approve' ? 'approved' : 'rejected'} successfully!`);
        setShowModal(false);
        fetchRequests();
      }
    } catch (error) {
      console.error('Error processing approval:', error);
      window.alert(error.response?.data?.error || 'Failed to process approval');
    } finally {
      setIsLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'priority-critical',
      'High': 'priority-high',
      'Medium': 'priority-medium',
      'Low': 'priority-low'
    };
    return colors[priority] || 'priority-low';
  };

  if (isLoading) {
    return (
      <div className="approval-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-approval-container">
      <div className="approval-header">
        <h1>Approval Dashboard</h1>
        <p>Review and manage maintenance requests</p>
      </div>

      {/* Tabs */}
      <div className="approval-tabs">
        <button
          className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending Approvals
          <span className="tab-badge">{pendingRequests.length}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'approved' ? 'active' : ''}`}
          onClick={() => setActiveTab('approved')}
        >
          Approved Requests
          <span className="tab-badge">{approvedRequests.length}</span>
        </button>
      </div>

      {activeTab === 'pending' && (
        <>
          {pendingRequests.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-check-circle empty-icon"></i>
              <h3>No Pending Requests</h3>
              <p>All maintenance requests have been reviewed</p>
            </div>
          ) : (
            <div className="requests-grid">
              {pendingRequests.map((request) => (
                <div key={request.id} className={`request-card ${getPriorityColor(request.priority)}`}>
                  <div className="request-header-card">
                    <div className="request-number">
                      <span className="badge badge-primary">#{request.ticket_number}</span>
                      <span className={`priority-badge ${request.priority.toLowerCase()}`}>
                        {request.priority}
                      </span>
                    </div>
                    <div className="request-date">
                      {new Date(request.request_date).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="request-body-card">
                    <h4 className="request-title">{request.issue_description}</h4>
                    <div className="request-meta">
                      <span><i className="fas fa-user"></i> {request.requested_by}</span>
                      <span><i className="fas fa-tag"></i> {request.maintenance_category}</span>
                      {request.equipment_maintenance && (
                        <span><i className="fas fa-tools"></i> {request.equipment_maintenance}</span>
                      )}
                      {request.facility_maintenance && (
                        <span><i className="fas fa-building"></i> {request.facility_maintenance}</span>
                      )}
                    </div>
                    {request.comments && (
                      <div className="request-comments">
                        <small><i className="fas fa-comment"></i> {request.comments}</small>
                      </div>
                    )}
                  </div>

                  <div className="request-actions">
                    <button
                      className="btn btn-success approve-btn"
                      onClick={() => handleApprove(request)}
                    >
                      <i className="fas fa-check"></i> Approve
                    </button>
                    <button
                      className="btn btn-danger reject-btn"
                      onClick={() => handleReject(request)}
                    >
                      <i className="fas fa-times"></i> Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'approved' && (
        <>
          {approvedRequests.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-inbox empty-icon"></i>
              <h3>No Approved Requests</h3>
              <p>Requests that have been approved will appear here</p>
            </div>
          ) : (
            <div className="requests-grid">
              {approvedRequests.map((request) => (
                <div key={request.id} className={`request-card approved ${getPriorityColor(request.priority)}`}>
                  <div className="request-header-card">
                    <div className="request-number">
                      <span className="badge badge-success">#{request.ticket_number}</span>
                      <span className={`priority-badge ${request.priority.toLowerCase()}`}>
                        {request.priority}
                      </span>
                    </div>
                    <div className="request-date">
                      {new Date(request.request_date).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="request-body-card">
                    <h4 className="request-title">{request.issue_description}</h4>
                    <div className="request-meta">
                      <span><i className="fas fa-user"></i> {request.requested_by}</span>
                      <span><i className="fas fa-check-circle"></i> Approved by {request.approved_by}</span>
                      {request.assigned_completion_date && (
                        <span><i className="fas fa-calendar"></i> Due: {request.assigned_completion_date}</span>
                      )}
                    </div>
                    {request.manager_comments && (
                      <div className="request-comments manager-comment">
                        <small><i className="fas fa-user-cog"></i> Manager: {request.manager_comments}</small>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Approval Modal */}
      {showModal && selectedRequest && (
        <div className="modal-overlay">
          <div className="approval-modal">
            <div className="modal-header">
              <h3>{actionType === 'approve' ? 'Approve' : 'Reject'} Request</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <div className="request-summary">
                <p><strong>Ticket:</strong> #{selectedRequest.ticket_number}</p>
                <p><strong>Issue:</strong> {selectedRequest.issue_description}</p>
                <p><strong>Requested By:</strong> {selectedRequest.requested_by}</p>
                <p><strong>Priority:</strong> {selectedRequest.priority}</p>
              </div>

              {actionType === 'approve' && (
                <div className="approval-fields">
                  <div className="form-group">
                    <label>Assigned Completion Date <span className="required">*</span></label>
                    <input
                      type="date"
                      className="form-control"
                      name="assigned_completion_date"
                      value={approvalData.assigned_completion_date}
                      onChange={handleApprovalChange}
                      min={new Date().toISOString().slice(0, 10)}
                    />
                    <small>Date by which this task should be completed</small>
                  </div>

                  <div className="form-group">
                    <label>Manager Comments</label>
                    <textarea
                      className="form-control"
                      name="manager_comments"
                      rows="3"
                      value={approvalData.manager_comments}
                      onChange={handleApprovalChange}
                      placeholder="Add any instructions or notes for the maintenance team..."
                    />
                  </div>
                </div>
              )}

              {actionType === 'reject' && (
                <div className="rejection-fields">
                  <div className="form-group">
                    <label>Rejection Reason <span className="required">*</span></label>
                    <textarea
                      className="form-control"
                      name="rejection_reason"
                      rows="4"
                      value={approvalData.rejection_reason}
                      onChange={handleApprovalChange}
                      placeholder="Please explain why this request is being rejected..."
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                className={`btn ${actionType === 'approve' ? 'btn-success' : 'btn-danger'}`}
                onClick={submitApproval}
                disabled={
                  isLoading ||
                  (actionType === 'approve' && !approvalData.assigned_completion_date) ||
                  (actionType === 'reject' && !approvalData.rejection_reason.trim())
                }
              >
                {isLoading ? 'Processing...' : actionType === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerApproval;